// components/sections/Features.js
import React from 'react';
import styled from 'styled-components';
import Container from '../common/Container';
import { SectionTitle, SectionSubtitle } from '../common/SharedStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faLightbulb,
    faShieldHalved,
    faClock
} from '@fortawesome/free-solid-svg-icons';

const FeaturesGrid = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
  margin-top: 3rem;
  margin-bottom: 6rem;

  @media (max-width: 768px) {
    gap: 2.5rem;
    margin-top: 2rem;
    margin-bottom: 5rem;
  }

  @media (max-width: 480px) {
    gap: 2rem;
    margin-top: 1.5rem;
    margin-bottom: 4rem;
  }
`;

const TopFeature = styled.div`
  width: 100%;
  max-width: 500px;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 1rem;
    box-sizing: border-box;
    margin-bottom: 1.5rem;
  }
`;

const BottomFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const FeatureCard = styled.div`
    padding: 2rem;
    background: ${props => props.isBestPriced ? 'rgba(240, 180, 50, 0.05)' : 'var(--card-background)'};
    border-radius: 8px;
    box-shadow: ${props => props.isBestPriced ? '0 2px 8px rgba(240, 180, 50, 0.2)' : 'var(--card-shadow)'};
    transition: all 0.3s ease;
    border: ${props => props.isBestPriced ? '1px solid var(--gold)' : 'none'};
    width: 100%;
    box-sizing: border-box;

    h3 {
        color: ${props => props.isBestPriced ? 'var(--gold)' : 'var(--gold3)'};
        margin-bottom: 0.5rem;
        font-size: 1.5rem;
        word-break: break-word;

        @media (max-width: 480px) {
            font-size: 1.3rem;
        }
    }

    p {
        color: var(--text-secondary);
        word-break: break-word;
    }

    &:hover {
        transform: translateY(-5px);
        box-shadow: ${props => props.isBestPriced ? '0 4px 12px rgba(240, 180, 50, 0.3)' : 'var(--card-shadow-hover)'};
    }

    @media (max-width: 768px) {
        padding: 1.5rem;
    }

    @media (max-width: 480px) {
        padding: 1.25rem;
    }
`;


const FeatureIcon = styled.div`
  font-size: 2rem;
  color: ${props => {
    if (props.index === 0) return 'var(--gold)';
    if (props.index === 1) return 'var(--gold2)';
    if (props.index === 2) return 'var(--gold3)';
    return 'var(--gold2)';
  }};
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }

  @media (max-width: 480px) {
    font-size: 1.6rem;
    margin-bottom: 0.75rem;
  }
`;

const SupplierNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 1rem;
  padding: 12px 20px;
  background-color: rgba(240, 180, 50, 0.1);
  border: 1px solid var(--gold);
  border-radius: 8px;
  font-size: 0.9rem;
  color: var(--text-primary);

  a {
    color: var(--gold);
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 10px 15px;
  }
`;

const Features = () => {
    const features = [
        {
            icon: faChartLine,
            title: "Best Priced Materials",
            description: "Find the best priced materials from the top suppliers in seconds"
        },
        {
            icon: faLightbulb,
            title: "Professional Quotes",
            description: "Confidently create accurate and professional quotes for your clients"
        },
        {
            icon: faShieldHalved,
            title: "Profit Calculation",
            description: "Calculate expenses, hourly rates, and profit margins for every project"
        },
        {
            icon: faClock,
            title: "Material Tracking",
            description: "Keep tabs on your material spending and generate job-by-job reports"
        }
    ];

    return (
        <section id="features">
            <Container>
                <SectionTitle>Features</SectionTitle>
                <SectionSubtitle>
                    Discover how Sivo's innovative features can transform your sign manufacturing business
                </SectionSubtitle>
                <FeaturesGrid>
                    <TopFeature>
                        {features.filter(feature => feature.title === "Best Priced Materials").map((feature, index) => (
                            <FeatureCard key={index} isBestPriced={true}>
                                <FeatureIcon index={0}>
                                    <FontAwesomeIcon icon={feature.icon} />
                                </FeatureIcon>
                                <h3>{feature.title}</h3>
                                <p>{feature.description}</p>
                            </FeatureCard>
                        ))}
                        <SupplierNotice>
                            <span>Is there a supplier/supply not in our database?</span>
                            <a href="mailto:support@signage-llc.com?subject=Supplier Request">Let us know!</a>
                        </SupplierNotice>
                    </TopFeature>
                    <BottomFeatures>
                        {features.filter(feature => feature.title !== "Best Priced Materials").map((feature, index) => (
                            <FeatureCard key={index} isBestPriced={false}>
                                <FeatureIcon index={index + 1}>
                                    <FontAwesomeIcon icon={feature.icon} />
                                </FeatureIcon>
                                <h3>{feature.title}</h3>
                                <p>{feature.description}</p>
                            </FeatureCard>
                        ))}
                    </BottomFeatures>
                </FeaturesGrid>
            </Container>
        </section>
    );
};

export default Features;
